import { http } from '@/lib/ats-https';
import type { APIKeys } from '@/types/requisitions';
import type { CloseRequisitionPayload } from '@/constants/requisitions/closeReasons';

/**
 * Helper to handle API responses with errors array
 * Throws error if response contains errors, otherwise returns data
 * Error format matches the error utility's expected structure
 */
const handleApiResponse = (response: any) => {
  const { data, errors = [] } = response || {};

  // Check if errors array exists and has items
  if (errors && Array.isArray(errors) && errors.length > 0) {
    // Create error object that matches error utility's expected format
    // The error utility expects error.errors array
    const error: any = new Error('API Error');
    error.errors = errors; // Attach errors array for getErrorMessage utility

    throw error;
  }

  // Return the data portion of the response
  return data || response.data;
};

const getRequisitionsFormFields = async () => {
  const response = await http.get(
    `/ats_custom_fields?subject_type=requisition`
  );
  return response.data;
};

// Job profiles list — the first page seeds the picker's default options
// (search is handled separately by the autoComplete's fetchConfig with `q`).
const getJobProfiles = async (params?: Record<string, any>) => {
  const response = await http.get(`/job_profiles`, {
    params: {
      items: 100,
      sort_key: 'created_at',
      sort_order: 'desc',
      ...params,
    },
  });
  return response.data;
};

// Job profile detail — used to prefill a requisition from a selected job profile.
const getJobProfile = async (jobProfileId: string) => {
  const response = await http.get(`/job_profiles/${jobProfileId}`);
  return response.data;
};

const createRequisition = async (payload: Record<string, any>) => {
  const response = await http.post(`/requisitions`, payload);
  // ats-https resolves 4xx with the response body (see auth-interceptor), so a
  // server-rejected create — e.g. an approval-reviewer "Users" field left empty
  // returns `err_approval_reviewer_field_empty` at 422 — comes back resolved
  // with an `errors` array. handleApiResponse re-throws it so callers surface
  // the error instead of treating the create as a success.
  return handleApiResponse(response);
};

const updateRequisition = async (
  requisitionId: string,
  payload: Record<string, any>
) => {
  const response = await http.put(`/requisitions/${requisitionId}`, payload);
  // Same 4xx-resolves-as-body handling as createRequisition (e.g. sending a
  // draft for approval with an empty reviewer field).
  return handleApiResponse(response);
};

const validateRequisitionChanges = async (
  requisitionId: string,
  payload: Record<string, any>
) => {
  const response = await http.post(`/requisitions/validate`, {
    ...payload,
    requisition_id: requisitionId,
  });
  return response.data;
};

const getRequisitions = async (params?: Record<string, any>) => {
  const response = await http.get(`/requisitions`, {
    params: { ...params, page: params?.page || 1, limit: params?.limit || 20 },
  });
  return response.data;
};

const getRequisition = async (requisitionId: string) => {
  const response = await http.get(`/requisitions/${requisitionId}`);
  return response.data;
};

const getRequisitionGroup = async (groupId: string) => {
  const response = await http.get(`/requisitions/group`, {
    params: { group_id: groupId },
  });
  return response.data;
};

const closeRequisition = async (
  requisitionId: string,
  payload: CloseRequisitionPayload
) => {
  const response = await http.put(
    `/requisitions/${requisitionId}/close`,
    payload
  );
  return handleApiResponse(response);
};

const archiveRequisition = async (
  requisitionId: string,
  payload?: Record<string, any>
) => {
  const response = await http.put(
    `/requisitions/${requisitionId}/archive`,
    payload
  );
  return handleApiResponse(response);
};

const makeRequisitionConfidential = async (
  requisitionId: string,
  payload?: Record<string, any>
) => {
  const response = await http.put(
    `/requisitions/${requisitionId}/toggle_confidential`,
    {
      confidential: true,
      ...payload,
    }
  );
  return handleApiResponse(response);
};

const makeRequisitionNonConfidential = async (
  requisitionId: string,
  payload?: Record<string, any>
) => {
  const response = await http.put(
    `/requisitions/${requisitionId}/toggle_confidential`,
    {
      confidential: false,
      ...payload,
    }
  );
  return handleApiResponse(response);
};

const openRequisition = async (
  requisitionId: string,
  payload?: Record<string, any>
) => {
  const response = await http.put(
    `/requisitions/${requisitionId}/open`,
    payload
  );
  return handleApiResponse(response);
};

const reopenRequisition = async (
  requisitionId: string,
  payload: { reopen_reason: string }
) => {
  const response = await http.put(
    `/requisitions/${requisitionId}/reopen`,
    payload
  );
  return handleApiResponse(response);
};

const updateRequisitionRecruiter = async (
  requisitionId: string,
  payload: { recruiter_id: number }
) => {
  const response = await http.put(
    `/requisitions/${requisitionId}/update_recruiter`,
    payload
  );
  return handleApiResponse(response);
};

const associateJobToRequisition = async (
  requisitionId: string,
  payload: {
    ats_job_id: number;
    apply_to_group?: boolean;
    need_reapproval?: boolean;
    reapproval_note?: string;
  }
) => {
  const response = await http.put(
    `/requisitions/${requisitionId}/associate_job`,
    payload
  );
  return handleApiResponse(response);
};

const cloneRequisition = async (requisitionId: string) => {
  const response = await http.post(`/requisitions/${requisitionId}/clone`);
  return handleApiResponse(response);
};

const getApprovalSettings = async () => {
  const response = await http.get(`/approvals/settings`);
  return response.data;
};

export const API_SERVICE = {
  getRequisitionsFormFields,
  getJobProfiles,
  getJobProfile,
  createRequisition,
  updateRequisition,
  validateRequisitionChanges,
  getRequisitions,
  closeRequisition,
  archiveRequisition,
  makeRequisitionConfidential,
  makeRequisitionNonConfidential,
  openRequisition,
  reopenRequisition,
  getRequisition,
  getRequisitionGroup,
  updateRequisitionRecruiter,
  associateJobToRequisition,
  cloneRequisition,
  getApprovalSettings,
};

export const API_KEYS: APIKeys = {
  getRequisitionsFormFields: 'requisitions/form-fields',
  getJobProfiles: 'requisitions/job-profiles',
  getJobProfile: 'requisitions/job-profile/:id',
  createRequisition: 'requisitions/create',
  updateRequisition: 'requisitions/update/:requisitionId',
  validateRequisitionChanges: 'requisitions/validate/:requisitionId',
  getRequisitions: 'requisitions/get',
  closeRequisition: 'requisitions/close',
  archiveRequisition: 'requisitions/archive',
  makeRequisitionConfidential: 'requisitions/make-confidential',
  makeRequisitionNonConfidential: 'requisitions/make-non-confidential',
  openRequisition: 'requisitions/open',
  reopenRequisition: 'requisitions/reopen',
  getRequisition: 'requisition/get',
  getRequisitionGroup: 'requisition/get-group',
  updateRequisitionRecruiter: 'requisitions/update-recruiter/:requisitionId',
  associateJobToRequisition: 'requisitions/associate-job/:requisitionId',
  cloneRequisition: 'requisitions/clone/:requisitionId',
  getApprovalSettings: 'approvals/settings',
};
